import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNotifications } from '../contexts/NotificationContext';

/**
 * Custom hook to fetch pending news requests globally and add them to notifications
 * This ensures notifications appear immediately on admin login, even before visiting news page
 */
export const useGlobalNewsNotifications = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const { addNotification, hasNotificationWithData } = useNotifications();

  // Check if we've already shown notifications for these requests (to prevent duplicates)
  const getProcessedRequests = useCallback(() => {
    try {
      const stored = localStorage.getItem('news_notifications_processed');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading processed notifications:', error);
      return [];
    }
  }, []);

  // Store processed request IDs to prevent duplicates
  const markRequestAsProcessed = useCallback((requestId) => {
    try {
      const processed = getProcessedRequests();
      const updated = [...new Set([...processed, requestId])]; // Ensure uniqueness
      localStorage.setItem('news_notifications_processed', JSON.stringify(updated));
    } catch (error) {
      console.error('Error storing processed notification:', error);
    }
  }, [getProcessedRequests]);

  // Fetch pending news requests and add notifications
  const fetchPendingNewsRequests = useCallback(async () => {
    if (isLoading || hasFetched) return;

    try {
      setIsLoading(true);
      const res = await axios.get(
        "https://aaapd.org.et/gallery_backend/news/getAllNewsForAdmin.php"
      );

      if (Array.isArray(res.data)) {
        const pendingRequests = res.data.filter(news => news.status === "pending");

        // Filter out requests that already have notifications
        const newRequests = pendingRequests.filter(request =>
          !hasNotificationWithData('newsId', request.id)
        );

        if (newRequests.length > 0) {
          newRequests.forEach((request) => {
            addNotification({
              type: 'news',
              title: 'Pending News Request',
              message: `${request.author || 'Someone'} submitted "${request.title || 'a news article'}" for approval`,
              data: {
                newsId: request.id,
                author: request.author,
                title: request.title,
                description: request.description,
                image: request.image
              }
            });

            // Mark this request as processed to prevent future duplicates
            markRequestAsProcessed(request.id);
          });
        }

        setHasFetched(true);
      }
    } catch (error) {
      console.error('Error fetching news requests for notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, hasFetched, addNotification, hasNotificationWithData, markRequestAsProcessed]);

  // Reset fetch state when needed (e.g., for re-fetching)
  const resetFetchState = useCallback(() => {
    setHasFetched(false);
  }, []);

  return {
    fetchPendingNewsRequests,
    resetFetchState,
    isLoading,
    hasFetched
  };
};
